import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { IMG } from "@/lib/brand";
import { PageHero, SectionEyebrow } from "@/components/Section";
import { HexWatermark } from "@/components/BrandMark";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/lib/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    country: "",
    industry: "",
    interest: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error(c.err_name);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(c.err_email);
      return;
    }
    if (form.message.trim().length < 10) {
      toast.error(c.err_message);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        country: form.country || null,
        industry: form.industry || null,
        interest: form.interest || null,
        message: form.message,
        website: form.website, // honeypot
      });
      toast.success(c.success);
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        country: "",
        industry: "",
        interest: "",
        message: "",
        website: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(c.err_submit);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <PageHero
        testId="contact-hero"
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        intro={c.hero_intro}
        image={IMG.lab2}
      />

      <section className="relative container-x py-24 sm:py-32 overflow-hidden">
        <HexWatermark
          opacity={0.04}
          size={620}
          strokeWidth={1.2}
          className="text-primary -left-40 top-24"
        />
        <div className="relative grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-12">
          <div>
            <SectionEyebrow>{c.hq_eyebrow}</SectionEyebrow>
            <div className="mt-6 space-y-5 text-primary">
              <div className="flex gap-4">
                <MapPin className="w-5 h-5 text-accent mt-1" />
                <div>
                  <div className="font-display text-lg">{c.hq_company}</div>
                  <div className="text-muted-foreground text-sm">{c.hq_addr}</div>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="w-5 h-5 text-accent mt-1" />
                <a href="mailto:info@baltivex.com" className="link-underline" data-testid="contact-email-link">
                  info@baltivex.com
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-10">
            <SectionEyebrow>{c.inquiry_eyebrow}</SectionEyebrow>
            <ul className="mt-6 space-y-3 text-muted-foreground text-sm">
              {c.inquiry_items.map((it, i) => (
                <li key={i}>{it}</li>
              ))}
            </ul>
          </div>

          <div className="img-zoom border border-border aspect-[4/3]">
            <img src={IMG.machinery} alt="Manufacturing" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="grid grid-cols-1 sm:grid-cols-2 gap-6 border border-border bg-white p-8 sm:p-10"
          >
            <div className="sm:col-span-2">
              <Label htmlFor="full_name" className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                {c.f_name}
              </Label>
              <Input
                id="full_name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="mt-2 rounded-none border-x-0 border-t-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary px-0 h-12"
                placeholder={c.f_name_ph}
                required
              />
            </div>

            {/* Honeypot — hidden from real users, bots fill it */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <label>
                Website (leave empty)
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => update("website", e.target.value)}
                  data-testid="contact-honeypot"
                />
              </label>
            </div>

            <div>
              <Label htmlFor="email" className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                {c.f_email}
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-2 rounded-none border-x-0 border-t-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary px-0 h-12"
                placeholder={c.f_email_ph}
                required
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                {c.f_phone}
              </Label>
              <Input
                id="phone"
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="mt-2 rounded-none border-x-0 border-t-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary px-0 h-12"
                placeholder={c.f_phone_ph}
              />
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="company" className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                {c.f_company}
              </Label>
              <Input
                id="company"
                data-testid="contact-input-company"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className="mt-2 rounded-none border-x-0 border-t-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary px-0 h-12"
                placeholder={c.f_company_ph}
              />
            </div>

            <div>
              <Label className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">{c.f_industry}</Label>
              <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                <SelectTrigger
                  data-testid="contact-select-industry"
                  className="mt-2 rounded-none border-x-0 border-t-0 border-b border-border focus:ring-0 px-0 h-12"
                >
                  <SelectValue placeholder={c.f_industry_ph} />
                </SelectTrigger>
                <SelectContent>
                  {c.industry_options.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`industry-opt-${o}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">{c.f_interest}</Label>
              <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                <SelectTrigger
                  data-testid="contact-select-interest"
                  className="mt-2 rounded-none border-x-0 border-t-0 border-b border-border focus:ring-0 px-0 h-12"
                >
                  <SelectValue placeholder={c.f_interest_ph} />
                </SelectTrigger>
                <SelectContent>
                  {c.interest_options.map((o) => (
                    <SelectItem key={o} value={o} data-testid={`interest-opt-${o}`}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="message" className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                {c.f_message}
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={6}
                className="mt-2 rounded-none border border-border focus-visible:ring-0 focus-visible:border-primary"
                placeholder={c.f_message_ph}
                required
              />
            </div>

            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
              <p className="text-xs text-muted-foreground max-w-md">{c.privacy}</p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-medium tracking-wide hover:bg-accent transition-colors disabled:opacity-60"
              >
                {submitting ? c.submitting : c.submit}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </form>
        </div>
        </div>
      </section>
    </div>
  );
}
